import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { Timeline } from 'primereact/timeline';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import { Chart } from 'primereact/chart';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import reinsuranceMockService from '../../../services/mockData/reinsuranceMockData';
import './style.scss';

const TreatyDetail = () => {
  const [treaty, setTreaty] = useState(null);
  const [cessions, setCessions] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCessionDialog, setShowCessionDialog] = useState(false);
  const [selectedCession, setSelectedCession] = useState(null);
  const toast = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    loadTreatyDetails();
  }, [id]);

  const loadTreatyDetails = async () => {
    setLoading(true);
    try {
      const treatyData = await reinsuranceMockService.getTreatyById(id);
      const cessionsData = await reinsuranceMockService.getCessionsByTreaty(id);
      const claimsData = await reinsuranceMockService.getClaimsByTreaty(id);

      setTreaty(treatyData);
      setCessions(cessionsData);
      setClaims(claimsData);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load treaty details'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCessionDetails = (cession) => {
    setSelectedCession(cession);
    setShowCessionDialog(true);
  };

  const statusBodyTemplate = (rowData) => {
    const severity = rowData.status === 'Active' ? 'success' :
                     rowData.status === 'Pending' ? 'warning' : 'danger';
    return <Tag value={rowData.status} severity={severity} />;
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-primary"
          tooltip="View Details"
          onClick={() => handleCessionDetails(rowData)}
        />
        <Button
          icon="pi pi-file-pdf"
          className="p-button-rounded p-button-text"
          tooltip="Generate Bordereau"
        />
      </div>
    );
  };

  const treatyChart = {
    labels: ['Capacity Used', 'Available'],
    datasets: [{
      data: [treaty?.utilization || 0, 100 - (treaty?.utilization || 0)],
      backgroundColor: ['#ff6384', '#36a2eb']
    }]
  };

  const chartOptions = {
    plugins: {
      legend: {
        position: 'bottom'
      }
    }
  };

  if (loading || !treaty) {
    return <div>Loading...</div>;
  }

  return (
    <div className="treaty-detail">
      <Toast ref={toast} />

      <div className="detail-header">
        <div className="header-left">
          <Button
            icon="pi pi-arrow-left"
            label="Back to Treaties"
            className="p-button-text"
            onClick={() => navigate('/reinsurance/treaties')}
          />
          <h2>{treaty.treatyNumber} - {treaty.type}</h2>
        </div>
        <div className="header-actions">
          <Button
            label="Edit Treaty"
            icon="pi pi-pencil"
            className="p-button-outlined"
          />
          <Button
            label="Generate Report"
            icon="pi pi-file-pdf"
            className="p-button-primary"
          />
        </div>
      </div>

      <div className="treaty-grid">
        <Card title="Treaty Information" className="treaty-info-card">
          <div className="info-grid">
            <div className="info-item">
              <label>Treaty Number:</label>
              <span>{treaty.treatyNumber}</span>
            </div>
            <div className="info-item">
              <label>Type:</label>
              <span>{treaty.type}</span>
            </div>
            <div className="info-item">
              <label>Reinsurer:</label>
              <span>{treaty.reinsurer}</span>
            </div>
            <div className="info-item">
              <label>Coverage:</label>
              <span>{treaty.coverage}</span>
            </div>
            <div className="info-item">
              <label>Limit:</label>
              <span>₱{treaty.limit?.toLocaleString()}</span>
            </div>
            <div className="info-item">
              <label>Retention:</label>
              <span>₱{treaty.retention?.toLocaleString()}</span>
            </div>
            <div className="info-item">
              <label>Period:</label>
              <span>{treaty.effectiveDate} to {treaty.expiryDate}</span>
            </div>
            <div className="info-item">
              <label>Status:</label>
              <Tag value={treaty.status} severity={treaty.status === 'Active' ? 'success' : 'warning'} />
            </div>
          </div>
        </Card>

        <Card title="Capacity Utilization" className="utilization-card">
          <div className="chart-container">
            <Chart type="doughnut" data={treatyChart} options={chartOptions} style={{ width: '250px' }} />
          </div>
          <Divider />
          <div className="utilization-details">
            <div className="detail-row">
              <span>Total Capacity:</span>
              <strong>₱{treaty.limit?.toLocaleString()}</strong>
            </div>
            <div className="detail-row">
              <span>Used Capacity:</span>
              <strong>₱{((treaty.limit * treaty.utilization) / 100)?.toLocaleString()}</strong>
            </div>
            <div className="detail-row">
              <span>Available:</span>
              <strong>₱{(treaty.limit * (100 - treaty.utilization) / 100)?.toLocaleString()}</strong>
            </div>
          </div>
        </Card>
      </div>

      <TabView>
        <TabPanel header="Cessions" leftIcon="pi pi-list">
          <DataTable
            value={cessions}
            paginator
            rows={10}
            loading={loading}
            className="cessions-table"
          >
            <Column field="cessionNumber" header="Cession No" sortable />
            <Column field="policyNumber" header="Policy" sortable />
            <Column field="insured" header="Insured" sortable />
            <Column field="sumInsured" header="Sum Insured" sortable
              body={(rowData) => `₱${rowData.sumInsured?.toLocaleString()}`} />
            <Column field="premium" header="Premium" sortable
              body={(rowData) => `₱${rowData.premium?.toLocaleString()}`} />
            <Column field="cededPercentage" header="Ceded %" sortable
              body={(rowData) => `${rowData.cededPercentage}%`} />
            <Column field="status" header="Status" body={statusBodyTemplate} />
            <Column header="Actions" body={actionBodyTemplate} />
          </DataTable>
        </TabPanel>

        <TabPanel header="Claims" leftIcon="pi pi-exclamation-triangle">
          <DataTable
            value={claims}
            paginator
            rows={10}
            loading={loading}
            className="claims-table"
          >
            <Column field="claimNumber" header="Claim No" sortable />
            <Column field="policyNumber" header="Policy" sortable />
            <Column field="dateOfLoss" header="Loss Date" sortable />
            <Column field="grossClaim" header="Gross Claim" sortable
              body={(rowData) => `₱${rowData.grossClaim?.toLocaleString()}`} />
            <Column field="recoverable" header="Recoverable" sortable
              body={(rowData) => `₱${rowData.recoverable?.toLocaleString()}`} />
            <Column field="status" header="Status" body={statusBodyTemplate} />
            <Column header="Actions" body={actionBodyTemplate} />
          </DataTable>
        </TabPanel>

        <TabPanel header="Documents" leftIcon="pi pi-file">
          <div className="documents-section">
            <div className="document-list">
              <div className="document-item">
                <i className="pi pi-file-pdf"></i>
                <span>Treaty Agreement.pdf</span>
                <Button icon="pi pi-download" className="p-button-text" />
              </div>
              <div className="document-item">
                <i className="pi pi-file-excel"></i>
                <span>Premium Bordereau Q3 2025.xlsx</span>
                <Button icon="pi pi-download" className="p-button-text" />
              </div>
              <div className="document-item">
                <i className="pi pi-file-pdf"></i>
                <span>Claims Bordereau Q3 2025.pdf</span>
                <Button icon="pi pi-download" className="p-button-text" />
              </div>
            </div>
            <Button
              label="Upload Document"
              icon="pi pi-upload"
              className="p-button-outlined"
            />
          </div>
        </TabPanel>
      </TabView>

      <Dialog
        header="Cession Details"
        visible={showCessionDialog}
        style={{ width: '50vw' }}
        onHide={() => setShowCessionDialog(false)}
      >
        {selectedCession && (
          <div className="cession-details">
            <div className="detail-grid">
              <div className="detail-item">
                <label>Cession Number:</label>
                <span>{selectedCession.cessionNumber}</span>
              </div>
              <div className="detail-item">
                <label>Policy Number:</label>
                <span>{selectedCession.policyNumber}</span>
              </div>
              <div className="detail-item">
                <label>Insured:</label>
                <span>{selectedCession.insured}</span>
              </div>
              <div className="detail-item">
                <label>Line of Business:</label>
                <span>{selectedCession.lineOfBusiness || 'Motor'}</span>
              </div>
              <div className="detail-item">
                <label>Sum Insured:</label>
                <span>₱{selectedCession.sumInsured?.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <label>Premium:</label>
                <span>₱{selectedCession.premium?.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <label>Ceded Percentage:</label>
                <span>{selectedCession.cededPercentage}%</span>
              </div>
              <div className="detail-item">
                <label>Ceded Amount:</label>
                <span>₱{((selectedCession.sumInsured * selectedCession.cededPercentage) / 100)?.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <label>Status:</label>
                <Tag value={selectedCession.status} severity={selectedCession.status === 'Active' ? 'success' : 'warning'} />
              </div>
            </div>
            <Divider />
            <div className="detail-actions">
              <Button label="View Policy" className="p-button-text" />
              <Button label="Generate Bordereau" className="p-button-outlined" />
              <Button label="Close" className="p-button-secondary" onClick={() => setShowCessionDialog(false)} />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default TreatyDetail;